import { useEffect, useRef, useState } from 'react';
import { FiMinus, FiPlus, FiEye, FiRotateCcw } from 'react-icons/fi';

const KEY_FONTE = 'ac_fonte';
const KEY_CONTRASTE = 'ac_contraste';
const PCTS = [100, 112, 125, 137];

function aplicarFonte(nivel: number) {
  document.documentElement.style.fontSize = `${PCTS[nivel] ?? 100}%`;
}
function aplicarContraste(on: boolean) {
  document.documentElement.classList.toggle('ac-alto-contraste', on);
}

/** Aplica as preferências salvas (chamar no boot do app). */
export function aplicarPreferenciasAcessibilidade() {
  aplicarFonte(Number(localStorage.getItem(KEY_FONTE) ?? '0'));
  aplicarContraste(localStorage.getItem(KEY_CONTRASTE) === '1');
}

/** Ícone internacional de acessibilidade (símbolo da pessoa com braços abertos). */
function IconeAcessibilidade({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z" />
    </svg>
  );
}

/**
 * Widget de acessibilidade: alto contraste + aumentar/reduzir fonte (Lei 13.460).
 * - variant="inline": botão para usar dentro do header, ao lado do sino.
 * - variant="fixo": botão flutuante (telas sem header, ex.: login/público).
 */
export function AcessibilidadeWidget({ variant = 'fixo' }: { variant?: 'fixo' | 'inline' }) {
  const inline = variant === 'inline';
  const [aberto, setAberto] = useState(false);
  const [iconeErro, setIconeErro] = useState(false);
  const [fonte, setFonte] = useState(() => Number(localStorage.getItem(KEY_FONTE) ?? '0'));
  const [contraste, setContraste] = useState(() => localStorage.getItem(KEY_CONTRASTE) === '1');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { aplicarFonte(fonte); localStorage.setItem(KEY_FONTE, String(fonte)); }, [fonte]);
  useEffect(() => { aplicarContraste(contraste); localStorage.setItem(KEY_CONTRASTE, contraste ? '1' : '0'); }, [contraste]);

  useEffect(() => {
    if (!aberto) return;
    const handle = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [aberto]);

  const painel = aberto && (
    <div className={`ac-panel ${inline ? 'ac-panel-inline' : 'ac-panel-fixo'}`} role="dialog" aria-label="Acessibilidade">
      <div className="ac-row">
        <span className="ac-label">Tamanho da fonte</span>
        <div className="ac-btns">
          <button onClick={() => setFonte((f) => Math.max(0, f - 1))} disabled={fonte === 0} aria-label="Reduzir fonte"><FiMinus size={15} /></button>
          <span className="ac-nivel">{PCTS[fonte]}%</span>
          <button onClick={() => setFonte((f) => Math.min(PCTS.length - 1, f + 1))} disabled={fonte === PCTS.length - 1} aria-label="Aumentar fonte"><FiPlus size={15} /></button>
        </div>
      </div>
      <button className="ac-toggle" onClick={() => setContraste((c) => !c)} aria-pressed={contraste}>
        <FiEye size={15} /> Alto contraste
        <span className={`ac-switch ${contraste ? 'on' : ''}`} />
      </button>
      {(fonte !== 0 || contraste) && (
        <button className="ac-reset" onClick={() => { setFonte(0); setContraste(false); }}>
          <FiRotateCcw size={13} /> Restaurar padrão
        </button>
      )}
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className={inline ? 'ac-inline' : 'ac-root'} ref={rootRef}>
        <button
          className={inline ? 'ac-btn-inline' : 'ac-fab'}
          onClick={() => setAberto((a) => !a)}
          aria-label="Acessibilidade"
          title="Acessibilidade"
        >
          {iconeErro
            ? <IconeAcessibilidade size={inline ? 30 : 34} />
            : <img src="/acessibilidade.png" alt="" className="ac-img" onError={() => setIconeErro(true)} />}
        </button>
        {painel}
      </div>
    </>
  );
}

const CSS = `
/* Botão inline (no header, ao lado do sino) */
.ac-inline { position: relative; display: inline-flex; }
.ac-btn-inline { display: flex; align-items: center; justify-content: center; height: 44px; width: 44px; padding: 5px; border-radius: 8px; background: transparent; border: none; color: #313b64; cursor: pointer; transition: background .15s, color .15s; }
.ac-btn-inline:hover { background: #f1f5f9; color: #313b64; }

/* Botão flutuante (telas sem header) */
.ac-root { position: fixed; right: 0.5rem; top: 50%; transform: translateY(-50%); z-index: 300; display: inline-flex; }
.ac-fab { width: 56px; height: 56px; padding: 8px; border-radius: 50%; background: #fff; border: 2px solid #313b64; color: #313b64; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(0,0,0,0.25); cursor: pointer; transition: transform .15s; }
.ac-fab:hover { transform: scale(1.06); }
.ac-img { width: 100%; height: 100%; object-fit: contain; }

/* Painel */
.ac-panel { width: 240px; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; box-shadow: 0 12px 40px rgba(0,0,0,0.18); padding: 0.875rem; display: flex; flex-direction: column; gap: 0.75rem; z-index: 320; }
.ac-panel-inline { position: absolute; right: 0; top: calc(100% + 8px); }
.ac-panel-fixo { position: absolute; right: calc(100% + 0.5rem); top: 50%; transform: translateY(-50%); }
.ac-row { display: flex; flex-direction: column; gap: 0.5rem; }
.ac-label { font-size: 0.8125rem; font-weight: 600; color: #334155; }
.ac-btns { display: flex; align-items: center; gap: 0.5rem; }
.ac-btns button { width: 34px; height: 34px; border-radius: 8px; border: 1px solid #E2E8F0; background: #F8FAFC; color: #334155; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.ac-btns button:hover:not(:disabled) { background: #EEF2F7; }
.ac-btns button:disabled { opacity: .4; cursor: not-allowed; }
.ac-nivel { flex: 1; text-align: center; font-size: 0.8125rem; font-weight: 700; color: #1E293B; font-variant-numeric: tabular-nums; }
.ac-toggle { display: flex; align-items: center; gap: 0.5rem; width: 100%; padding: 0.625rem 0.75rem; border: 1px solid #E2E8F0; border-radius: 10px; background: #F8FAFC; color: #334155; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
.ac-switch { margin-left: auto; width: 34px; height: 18px; border-radius: 9px; background: #CBD5E1; position: relative; transition: background .15s; }
.ac-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; transition: left .15s; }
.ac-switch.on { background: #313b64; }
.ac-switch.on::after { left: 18px; }
.ac-reset { display: flex; align-items: center; justify-content: center; gap: 0.375rem; font-size: 0.75rem; color: #64748B; background: none; border: none; cursor: pointer; padding: 0.25rem; }
.ac-reset:hover { color: #313b64; text-decoration: underline; }

/* Alto contraste: modo preto e branco (escala de cinza + contraste reforçado em toda a página). */
.ac-alto-contraste { filter: grayscale(1) contrast(1.25); }
.ac-alto-contraste a { text-decoration: underline; }
.ac-alto-contraste *:focus-visible { outline: 3px solid #000 !important; outline-offset: 2px; }
`;
